// Compact stylized "heatmap" of all 81 Turkish cities arranged by 7 regions.
// No external map library — pure SVG/CSS for performance and full styling control.
import React, { useMemo, useState } from "react";

// Cities grouped by Türkiye's geographical regions
const REGIONS = [
    {
        name: "Marmara",
        cities: [
            ["34", "İstanbul"], ["59", "Tekirdağ"], ["22", "Edirne"], ["39", "Kırklareli"],
            ["41", "Kocaeli"], ["54", "Sakarya"], ["77", "Yalova"], ["16", "Bursa"],
            ["10", "Balıkesir"], ["17", "Çanakkale"], ["11", "Bilecik"],
        ],
    },
    {
        name: "Ege",
        cities: [
            ["35", "İzmir"], ["09", "Aydın"], ["48", "Muğla"], ["20", "Denizli"],
            ["64", "Uşak"], ["43", "Kütahya"], ["03", "Afyonkarahisar"], ["45", "Manisa"],
        ],
    },
    {
        name: "Akdeniz",
        cities: [
            ["07", "Antalya"], ["32", "Isparta"], ["15", "Burdur"], ["33", "Mersin"],
            ["01", "Adana"], ["31", "Hatay"], ["46", "Kahramanmaraş"], ["80", "Osmaniye"],
        ],
    },
    {
        name: "İç Anadolu",
        cities: [
            ["06", "Ankara"], ["42", "Konya"], ["38", "Kayseri"], ["50", "Nevşehir"],
            ["68", "Aksaray"], ["51", "Niğde"], ["70", "Karaman"], ["71", "Kırıkkale"],
            ["40", "Kırşehir"], ["66", "Yozgat"], ["58", "Sivas"], ["26", "Eskişehir"],
        ],
    },
    {
        name: "Karadeniz",
        cities: [
            ["55", "Samsun"], ["61", "Trabzon"], ["52", "Ordu"], ["28", "Giresun"],
            ["53", "Rize"], ["08", "Artvin"], ["57", "Sinop"], ["37", "Kastamonu"],
            ["18", "Çankırı"], ["19", "Çorum"], ["60", "Tokat"], ["05", "Amasya"],
            ["14", "Bolu"], ["67", "Zonguldak"], ["74", "Bartın"], ["78", "Karabük"],
            ["81", "Düzce"], ["29", "Gümüşhane"], ["69", "Bayburt"],
        ],
    },
    {
        name: "Doğu Anadolu",
        cities: [
            ["25", "Erzurum"], ["44", "Malatya"], ["62", "Tunceli"], ["23", "Elazığ"],
            ["12", "Bingöl"], ["49", "Muş"], ["13", "Bitlis"], ["65", "Van"],
            ["30", "Hakkari"], ["04", "Ağrı"], ["75", "Ardahan"], ["36", "Kars"],
            ["76", "Iğdır"], ["24", "Erzincan"],
        ],
    },
    {
        name: "Güneydoğu Anadolu",
        cities: [
            ["27", "Gaziantep"], ["79", "Kilis"], ["02", "Adıyaman"], ["63", "Şanlıurfa"],
            ["47", "Mardin"], ["72", "Batman"], ["73", "Şırnak"], ["56", "Siirt"],
            ["21", "Diyarbakır"],
        ],
    },
];

// Map a value (0..max) to a color from teal-low to gold-high via a green/orange ramp
function valueToColor(value, max) {
    if (max <= 0) return "#1f3550";
    const t = Math.max(0, Math.min(1, value / max));
    // Cool to warm: deep-blue (low) → teal (mid) → gold (high)
    const stops = [
        [0, [18, 59, 82]],     // deep-blue
        [0.5, [57, 200, 193]], // teal
        [1, [214, 176, 107]],  // gold
    ];
    let lo = stops[0], hi = stops[stops.length - 1];
    for (let i = 0; i < stops.length - 1; i++) {
        if (t >= stops[i][0] && t <= stops[i + 1][0]) {
            lo = stops[i];
            hi = stops[i + 1];
            break;
        }
    }
    const range = hi[0] - lo[0] || 1;
    const k = (t - lo[0]) / range;
    const r = Math.round(lo[1][0] + (hi[1][0] - lo[1][0]) * k);
    const g = Math.round(lo[1][1] + (hi[1][1] - lo[1][1]) * k);
    const b = Math.round(lo[1][2] + (hi[1][2] - lo[1][2]) * k);
    return `rgb(${r},${g},${b})`;
}

export default function TurkeyHeatmap({ values = {}, unit = "%", title }) {
    // values: { "34": 78.3, "07": 75.2, ... }
    const max = useMemo(
        () =>
            Math.max(
                0,
                ...Object.values(values || {}).map((v) =>
                    typeof v === "number" ? v : 0
                ),
            ),
        [values],
    );
    const [hover, setHover] = useState(null);

    return (
        <div data-testid="turkey-heatmap" className="w-full">
            {title && (
                <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-dark-navy uppercase tracking-wider">
                        {title}
                    </h3>
                    <Legend />
                </div>
            )}
            <div className="space-y-4">
                {REGIONS.map((reg) => (
                    <div key={reg.name}>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-deep-blue/60 mb-1.5 font-semibold">
                            {reg.name}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {reg.cities.map(([code, name]) => {
                                const v = values[code];
                                const has = typeof v === "number";
                                const color = has ? valueToColor(v, max || 1) : "#e2e8f0";
                                const isHovered = hover === code;
                                return (
                                    <div
                                        key={code}
                                        onMouseEnter={() => setHover(code)}
                                        onMouseLeave={() => setHover(null)}
                                        data-testid={`city-tile-${code}`}
                                        className={`relative w-[68px] h-[58px] rounded-md flex flex-col items-center justify-center cursor-pointer text-[11px] leading-tight font-medium ${
                                            isHovered ? "ring-2 ring-teal scale-[1.04]" : ""
                                        }`}
                                        style={{ backgroundColor: color, color: has ? "#08111d" : "#94a3b8" }}
                                        title={`${name} (${code})${has ? ` — ${v}${unit}` : ""}`}
                                    >
                                        <span className="font-data text-[10px] opacity-80">{code}</span>
                                        <span className="text-[10px] truncate w-full px-1 text-center">
                                            {name}
                                        </span>
                                        {has && (
                                            <span className="font-data text-[10px] font-bold">
                                                {Math.round(v * 10) / 10}
                                                {unit}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Legend() {
    return (
        <div className="flex items-center gap-2 text-[10px] text-deep-blue/70">
            <span className="uppercase tracking-wider">Düşük</span>
            <span
                className="inline-block h-2 w-32 rounded-sm"
                style={{
                    background:
                        "linear-gradient(to right, rgb(18,59,82), rgb(57,200,193), rgb(214,176,107))",
                }}
            />
            <span className="uppercase tracking-wider">Yüksek</span>
        </div>
    );
}
